/**
 * Builds tw_user_resolved dataLayer payload (TP-142).
 * Pure helpers — no dataLayer side effects.
 */

export function mapUserType(type) {
  if (type === 'buyer') {
    return 'client';
  }
  if (type === 'seller') {
    return 'freelancer';
  }
  return null;
}

/**
 * PostHog-friendly person properties (no tw_ prefix).
 * Passed as tw_user_traits so GTM can posthog.identify(id, traits) in one shot.
 */
export function buildUserTraits(user, emailSha256) {
  const roleInterests = user.role_interests;

  return {
    email_sha256: emailSha256,
    user_type: mapUserType(user.type),
    icp_type: user.icp_type != null ? user.icp_type : null,
    icp_type_source: user.icp_type_source != null ? user.icp_type_source : null,
    icp_confidence: user.icp_confidence != null ? user.icp_confidence : null,
    company_size: user.company_size != null ? user.company_size : null,
    company_domain: user.company_domain != null ? user.company_domain : null,
    company_industry: user.company_industry != null ? user.company_industry : null,
    company_funding_stage:
      user.company_funding_stage != null ? user.company_funding_stage : null,
    company_funding_last_raised_at:
      user.company_funding_last_raised_at != null
        ? user.company_funding_last_raised_at
        : null,
    first_touch_source:
      user.first_touch_source != null ? user.first_touch_source : null,
    first_touch_campaign:
      user.first_touch_campaign != null ? user.first_touch_campaign : null,
    first_touch_at: user.first_touch_at != null ? user.first_touch_at : null,
    intent_at_signup:
      user.intent_at_signup != null ? user.intent_at_signup : null,
    jobs_posted_count:
      user.jobs_posted_count != null ? user.jobs_posted_count : null,
    hires_made_count:
      user.hires_made_count != null ? user.hires_made_count : null,
    is_pro: typeof user.pro === 'boolean' ? user.pro : null,
    role_interests: Array.isArray(roleInterests) ? roleInterests.slice() : [],
  };
}

export async function buildUserResolvedPayload(user) {
  const emailSha256 = await hashEmailSha256(user.email);
  const traits = buildUserTraits(user, emailSha256);

  return {
    event: 'tw_user_resolved',
    tw_user_id: user.id != null ? `usr_${user.id}` : null,
    tw_user_traits: traits,
  };
}

function digestToHex(digest) {
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * SHA-256 of normalized email (lowercase, trimmed). Never returns raw email.
 */
async function hashEmailSha256(email) {
  const normalized = (email || '').trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const data = new TextEncoder().encode(normalized);
      const digest = await crypto.subtle.digest('SHA-256', data);
      return digestToHex(digest);
    }

    if (typeof process !== 'undefined' && process.versions && process.versions.node) {
      // eslint-disable-next-line global-require
      const nodeCrypto = require('crypto');
      return nodeCrypto.createHash('sha256').update(normalized, 'utf8').digest('hex');
    }
  } catch (error) {
    console.error('Failed to hash email for tracking:', error);
    return null;
  }

  return null;
}
